<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { NSpin, NAlert, NTag, useMessage } from 'naive-ui'
import FleetTable from '@/components/fleet/FleetTable.vue'
import FleetCards from '@/components/fleet/FleetCards.vue'
import SegmentedButton from '@/components/commons/SegmentedButton.vue'
import PageHeader from '@/components/commons/PageHeader.vue'
import VehicleFormModal from '@/components/fleet/VehicleFormModal.vue'
import DeleteConfirmDialog from '@/components/fleet/DeleteConfirmDialog.vue'
import { useVehiclesList, useDeleteVehicle, type Vehicle } from '@/api/vehicles'
import { useDevicesList } from '@/api/devices'
import ExportButton from '@/components/commons/ExportButton.vue'

const { data, isLoading, isError, error } = useVehiclesList()
const { data: devicesData } = useDevicesList()
const deleteMut = useDeleteVehicle()
const message = useMessage()

const freeDevices = computed(() => devicesData.value?.devices.filter((d) => !d.vehicle_id) ?? [])

type View = 'cards' | 'table'
const STORAGE_KEY = 'dozor_fleet_view'

const view = ref<View>(((): View => {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'table' ? 'table' : 'cards'
  } catch {
    return 'cards'
  }
})())

watch(view, (v) => {
  try { localStorage.setItem(STORAGE_KEY, v) } catch { /* ignore */ }
}, { flush: 'sync' })

// Mobile breakpoint forces cards view
const isMobile = ref(false)
function updateMobile() {
  isMobile.value = window.matchMedia('(max-width: 767px)').matches
}
onMounted(() => {
  updateMobile()
  window.addEventListener('resize', updateMobile)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateMobile)
})

const effectiveView = computed<View>(() => (isMobile.value ? 'cards' : view.value))
const VIEW_OPTIONS = [
  { value: 'cards' as const, label: 'Карточки' },
  { value: 'table' as const, label: 'Таблица' },
]

const formMode = ref<'create' | 'edit'>('create')
const formShow = ref(false)
const formVehicle = ref<Vehicle | null>(null)

const deleteShow = ref(false)
const deleteVehicle = ref<Vehicle | null>(null)

function openCreate() {
  formMode.value = 'create'
  formVehicle.value = null
  formShow.value = true
}
function openEdit(v: Vehicle) {
  formMode.value = 'edit'
  formVehicle.value = v
  formShow.value = true
}
function onCreated(v: Vehicle) {
  message.success(`Машина "${v.name}" создана`)
}
function onUpdated(v: Vehicle) {
  message.success(`Машина "${v.name}" обновлена`)
}
function openDelete(v: Vehicle) {
  deleteVehicle.value = v
  deleteShow.value = true
}
async function onDeleteConfirm() {
  if (!deleteVehicle.value) return
  const v = deleteVehicle.value
  try {
    await deleteMut.mutateAsync(v.id)
    message.success(`Машина "${v.name}" удалена`)
    deleteShow.value = false
    deleteVehicle.value = null
  } catch (e: unknown) {
    message.error(`Ошибка удаления: ${(e as Error)?.message ?? 'неизвестная'}`)
  }
}
</script>

<template>
  <div class="fleet-page">
    <PageHeader title="Автопарк">
      <template #actions>
        <n-tag v-if="devicesData" round :bordered="false" type="info">
          Девайсы: {{ devicesData.total }} · свободно {{ freeDevices.length }}
        </n-tag>
        <ExportButton path="/export/fleet.xlsx" :params="{}" :filename="`dozor-fleet-${new Date().toISOString().slice(0,10)}.xlsx`" />
      </template>
    </PageHeader>

    <n-spin :show="isLoading">
      <n-alert v-if="isError" type="error" style="margin-bottom: 12px">
        Ошибка загрузки списка: {{ error?.message ?? 'неизвестная' }}
      </n-alert>
      <component
        :is="effectiveView === 'cards' ? FleetCards : FleetTable"
        v-if="data"
        :vehicles="data.vehicles"
        @add="openCreate"
        @edit="openEdit"
        @delete="openDelete"
      >
        <template #view-toggle>
          <SegmentedButton
            v-if="!isMobile"
            v-model:modelValue="view"
            :options="VIEW_OPTIONS"
          />
        </template>
      </component>
    </n-spin>

    <VehicleFormModal
      v-model:show="formShow"
      :mode="formMode"
      :vehicle="formVehicle"
      :free-devices="freeDevices"
      @created="onCreated"
      @updated="onUpdated"
    />

    <DeleteConfirmDialog
      v-if="deleteVehicle"
      v-model:show="deleteShow"
      :vehicle-name="deleteVehicle.name"
      @confirm="onDeleteConfirm"
    />
  </div>
</template>

<style scoped>
.fleet-page { padding: var(--sp-5); }
@media (max-width: 767px) {
  .fleet-page { padding: var(--sp-4); }
}
</style>
